import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsPhone } from './task/use-viewport';

const FOCUSABLE = 'button, [href], input, select, textarea, canvas[tabindex], [tabindex]:not([tabindex="-1"])';

/**
 * The `editor` layout's box, matching the two formula editors.
 *
 * Wider than a form dialog because an editor's bands are rows of controls rather than a column
 * of fields, and 720 rather than their 780 because this one's bands are a toolbar and a list,
 * not a toolbar and a palette grid.
 */
const EDITOR_MAX_WIDTH = 'max-w-[860px]';
const EDITOR_MAX_HEIGHT = 720;
/**
 * A FLOOR, so a nearly-empty editor is still a dialog and not a strip.
 *
 * Tuned against the chrome: a title band, a toolbar, and two bands at the foot come to roughly
 * 260px before any content, so this leaves a little over a row's worth of body visible.
 */
const EDITOR_MIN_HEIGHT = 380;
const isVisible = (el: HTMLElement) => !el.hasAttribute('disabled') && el.offsetParent !== null;

/**
 * Accessible modal shell — matches the app's block-modal convention:
 * `role="dialog"` + `aria-modal`, labelled by its heading, closes on Escape / backdrop, traps Tab
 * focus, focuses the first control on open, and restores focus to the trigger on close.
 */
export default function ModalShell({
  title,
  onClose,
  children,
  maxWidth = 'max-w-md',
  icon,
  fullBleedOnPhone = false,
  bottomSheetOnPhone = false,
  contained = false,
  zClass = 'z-50',
  initialFocusRef,
  returnFocusRef,
  headerActions,
  subheader,
  footer,
  layout = 'form',
}: {
  /**
   * Rendered into the `<h2>` the dialog is labelled by, so a node is as good as a string — the
   * accessible name comes from the element's text either way. A promotion names the variable it
   * is about and sets it in the chip colour, which a plain string cannot do.
   */
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  /**
   * A glyph before the title, for a dialog whose job is legible at a glance: a bin for a delete,
   * a warning triangle for a confirm, a tick for a success.
   *
   * Decorative by contract — the title says the same thing in words, so the caller passes an
   * `aria-hidden` icon and nothing is lost when it is not rendered. It lives here rather than in
   * each caller's own header because the header is the half of these dialogs the shell owns; a
   * caller that drew its own just to keep its icon would be back to a hand-rolled modal.
   */
  icon?: ReactNode;
  /**
   * Controls in the title bar, left of the close button.
   *
   * For a dialog that is an EDITOR rather than a form: the two formula editors both put undo,
   * redo and close together in that corner, and a teacher who has learned the corner expects it
   * wherever they are editing. They hand-roll their own shells, so this is where a dialog that
   * uses the shell gets the same corner instead of a sixth hand-rolled header.
   */
  headerActions?: ReactNode;
  /**
   * A band between the title bar and the scrolling body — OUTSIDE the scroll.
   *
   * For a pinned toolbar. The alternative is `sticky top-0` inside `children`, and that leaks:
   * the scrollport's top edge is the body's PADDING edge, so content slides through the few
   * pixels above a stuck element before it disappears under it. Covering that gap takes a
   * negative offset tuned to this component's padding, from a caller that cannot see it.
   * Out here there is no gap to cover, because the body starts below the band.
   */
  subheader?: ReactNode;
  /**
   * The bottom band, below the scrolling body — `layout='editor'` only.
   *
   * In `form` a dialog's buttons are the last thing in the stack and scroll with it, which is
   * right for a short form. An editor's do not: the body is a list that grows, and Done must not
   * walk off the end of it.
   */
  footer?: ReactNode;
  /**
   * WHICH DIALOG THIS IS, and the two are genuinely different objects.
   *
   * `form` is the default and what twenty-odd callers use: a padded box, children stacked with a
   * gap, a bold title. Right for a confirm, a short form, a picker.
   *
   * `editor` is the shape the equation and reaction editors arrived at independently and now
   * share almost byte for byte: edge-to-edge horizontal BANDS, each one `shrink-0 px-4` with its
   * own vertical padding, separated by a rule and nothing else — no gaps, no insets, no floating
   * cards. A quieter title, a wider box, a rounder corner, and a PINNED height, so the footer
   * stays where the hand left it however much the body grows.
   *
   * One component rather than a second shell, because everything above the chrome — the focus
   * trap, Escape, the restore, the badge layer, the labelling — is identical and is the half
   * that is expensive to get wrong. Those two editors hand-roll all of it today; this is what
   * they can move onto.
   */
  layout?: 'form' | 'editor';
  /** Fill the screen below `md`, the way the block editors do. Worth it for a dialog whose content
   * grows with the task — a list of a dozen rows in a centred card on a phone is a card with no
   * room to be a card. */
  fullBleedOnPhone?: boolean;
  /**
   * Cover the nearest positioned ancestor instead of the viewport.
   *
   * For a dialog that belongs to one REGION of the page rather than to the page. Preview's
   * submission summary is the case it was built for: Preview exists to look like a student's screen,
   * and a student has no accessibility review panel for a dialog to be greying out. The caller has
   * to give the region `position: relative`, the same contract any absolutely-positioned child has.
   *
   * The focus trap is unchanged and still absolute — a dialog that covers part of the page is still
   * modal, and Tab must not walk out of it into the part it does not cover.
   */
  contained?: boolean;
  /**
   * Rise from the bottom edge on a phone, square across the top, and centre from `sm` up.
   *
   * The convention the task-level confirms already used, kept because it is the right one for a
   * short destructive question on a phone: the buttons land under the thumb instead of mid-screen.
   * `fullBleedOnPhone` is the other phone treatment and they are exclusive — that one is for a
   * dialog whose content grows with the task, this one for a dialog that is two lines and a choice.
   */
  bottomSheetOnPhone?: boolean;
  /**
   * The stacking layer, for a dialog opened from a surface that already sits above the default one.
   *
   * The Flow panel is a `z-[110]` full-screen dialog on a phone, so a `z-50` modal portaled to
   * <body> from inside it renders UNDERNEATH the panel that opened it. A prop rather than each
   * such caller keeping its own hand-built shell, which is how both of Flow's dialogs came to have
   * their own overlay, tab trap and close button.
   */
  zClass?: string;
  /**
   * Which control to focus on open, when "the first one" is wrong.
   *
   * The default picks the first focusable thing that is not the close button, which suits a form.
   * It does not suit a dialog whose controls are a PICTURE of something — the map picker's first
   * control is the first task on the board, and focusing it made the map draw a flow line to it, so
   * the dialog opened proposing a destination nobody had chosen.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * WHERE FOCUS GOES ON CLOSE, when there is nothing to put it back to.
   *
   * The shell captures `document.activeElement` at mount and restores it on unmount, which is
   * right whenever a control opened the dialog and survived. It is not always true: the preview
   * verdict dialog is raised by a SUBMIT, and the bar re-renders during that submission, so by
   * the time the shell mounts the captured element is already `document.body`. Restoring that
   * is restoring nothing — focus landed on the body and a keyboard user lost their place
   * entirely. Measured, with a real Enter on the button rather than a synthetic click.
   *
   * Consulted only when the capture is worthless (absent, or the body itself). A caller that
   * was opened normally keeps the ordinary behaviour, so this cannot move focus somewhere
   * surprising in the fifteen dialogs that never needed it.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Same trick this file already uses for onClose: the focus effect runs once, on open, so it reads
  // the caller's ref through a ref of its own rather than taking it as a dependency and re-firing.
  const initialFocusRefRef = useRef(initialFocusRef);
  initialFocusRefRef.current = initialFocusRef;
  const returnFocusRefRef = useRef(returnFocusRef);
  returnFocusRefRef.current = returnFocusRef;
  const isEditor = layout === 'editor';
  /* The shared query, not a fourth copy of it — see `use-viewport`'s own note on the three that
     had already drifted. A phone gets the measured viewport; anything else gets the cap. */
  const isPhone = useIsPhone();

  /*
   * THE VISUAL viewport, which on a phone is what the keyboard leaves behind.
   *
   * `dvh` is no help there — iOS does not shrink it when the keyboard opens, so a footer sized
   * against it sits underneath the keys. Only measured for `editor`, which is the layout that
   * pins its height; a form dialog is capped and sized by its content and has nothing to do
   * with this number.
   */
  const [viewportH, setViewportH] = useState<number | null>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!isEditor || !vv) return;
    const read = () => setViewportH(vv.height);
    read();
    vv.addEventListener('resize', read);
    return () => vv.removeEventListener('resize', read);
  }, [isEditor]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const el = dialogRef.current;
      const focusables = el ? Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible) : [];
      // Prefer what the caller named, then the first real control (a field/action) over the header
      // close button.
      const target = initialFocusRefRef.current?.current
        ?? focusables.find((f) => f !== closeBtnRef.current)
        ?? focusables[0]
        ?? el;
      target?.focus();
    });

    // Escape-to-close + Tab focus-trap, registered on the document (capture) to match the app's
    // dialog convention — keeps the trap off the non-interactive dialog element itself.
    function onKeyDown(e: KeyboardEvent) {
      const el = dialogRef.current;
      if (!el) return;
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const f = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
      if (f.length === 0) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      /* The capture, unless it is worthless -- see `returnFocusRef`. */
      const captured = previouslyFocused && previouslyFocused !== document.body
        ? previouslyFocused
        : returnFocusRefRef.current?.current ?? null;
      captured?.focus?.();
    };
  }, []);

  return (
    <div
      role='presentation'
      className={`${contained ? 'absolute' : 'fixed'} inset-0 ${zClass} flex justify-center bg-ws-modal-overlay ${
        isEditor
          // Stretched on a phone so the dialog can fill what the keyboard leaves it, and room
          // around it anywhere else — the two formula editors' own overlay, exactly.
          //
          // `isPhone`, NOT a `md:` breakpoint. A phone in landscape is 844px wide and sails
          // past `md`, so the width test handed it the desktop treatment: a centred card on a
          // 390px-tall screen, capped at 94dvh, leaving a few hundred pixels to edit in. The
          // shared query knows a short coarse-pointer screen is a phone; a breakpoint cannot.
          ? (isPhone ? 'items-stretch p-0' : 'items-center p-4')
          : bottomSheetOnPhone
            ? 'items-end p-0 sm:items-center sm:p-2'
            : `items-center ${fullBleedOnPhone ? 'p-0 md:p-2' : 'p-2'}`
      }`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        /*
         * Capped and scrollable. There was no height limit at all, so a dialog taller than the
         * screen simply ran off both ends of a viewport that centres it — nothing could scroll, and
         * the buttons at the bottom became unreachable. Phone landscape hits that at a few hundred
         * pixels of content.
         */
        className={isEditor
          // No padding and no gap: in this layout the CALLER supplies bands, and a band pads
          // itself and is separated by its own rule. Padding here would inset every one of them
          // and turn the set into a stack of floating cards, which is the look this layout
          // exists to get away from.
          ? `relative flex w-full flex-col overflow-hidden bg-ws-elevated shadow-2xl ${
              isPhone ? 'border-0' : `${EDITOR_MAX_WIDTH} rounded-2xl border border-ws-border`}`
          : `relative flex w-full flex-col gap-5 border border-ws-border bg-ws-elevated shadow-xl ${
              fullBleedOnPhone
                ? `h-full rounded-none p-4 md:h-auto md:max-h-[calc(100dvh-1rem)] md:rounded-lg md:p-6 ${maxWidth}`
                // Contained, the cap is the REGION and not the viewport — a dvh cap inside a box shorter
                // than the screen lets the dialog overflow the thing it is supposed to be sitting in.
                : `${contained ? 'max-h-full' : 'max-h-[calc(100dvh-1rem)]'} p-6 ${maxWidth} ${
                    bottomSheetOnPhone ? 'rounded-t-2xl sm:rounded-lg' : 'rounded-lg'}`
            }`}
        /*
         * A FLOOR AND A CAP, not a pinned height — and the distinction was worth measuring.
         *
         * Pinning is what the two formula editors do, and it is right for them: their body is a
         * palette grid that always fills whatever it is given, so a fixed box is always full.
         * This one's body is a LIST, and a list ranges from one 60px row to five blocks with
         * images. Pinned at the size the full case needs, the common case — one or two
         * paragraphs — was a 677px dialog holding 60px of content and 320px of nothing.
         *
         * What the pin was really protecting is that the footer must not scroll away, and that
         * comes from `footer` being a SIBLING of the scrolling body rather than the last thing
         * in it. It holds at any height. The box still grows as blocks are added, but only up
         * to the cap and only while someone is deliberately adding them, which is a moment you
         * expect a box to change size.
         *
         * A phone takes the measured viewport, since that is what the keyboard leaves.
         */
        style={!isEditor ? undefined
          : isPhone
            ? { height: viewportH ? `${viewportH}px` : '100dvh' }
            : { minHeight: `min(94dvh, ${EDITOR_MIN_HEIGHT}px)`, maxHeight: `min(94dvh, ${EDITOR_MAX_HEIGHT}px)` }}
      >
        {/* Where Preview's read-through pins its numbered badges when this dialog is the surface
            being reviewed. Empty and inert; it costs one div and it is what lets the emulator
            number a modal's rows the way it numbers a task's, instead of listing 1..6 against a
            screen showing no numbers at all. A permanent node is also what makes a portal into it
            safe — React never has to reconcile a container it did not create. */}
        <div data-a11y-badge-layer='' aria-hidden='true' className='pointer-events-none absolute inset-0 z-30' />
        {/* THE TITLE BAND. Quieter in `editor` — `text-sm font-semibold` against the form
            layout's `text-lg font-bold` — because an editor's title is a label on an instrument
            you are about to use, not the question a confirm is asking. The band is edge to edge
            and the rule under it is what separates it; in `form` the gap does that. */}
        <div className={`flex shrink-0 items-start justify-between gap-2 ${
          isEditor ? 'items-center border-b border-ws-border px-4 py-3' : ''}`}
        >
          <div className='flex min-w-0 items-center gap-2'>
            {icon}
            <h2 id={titleId} className={`truncate ${isEditor ? 'text-sm font-semibold text-ws-text' : 'text-lg font-bold text-ws-text'}`}>{title}</h2>
          </div>
          <div className='flex shrink-0 items-center gap-1'>
            {headerActions}
            <button ref={closeBtnRef} type='button' onClick={onClose} aria-label={t('common.close')} className='flex h-7 w-7 items-center justify-center rounded-lg text-ws-muted hover:bg-ws-hover'>
              <X className='h-4 w-4' />
            </button>
          </div>
        </div>
        {subheader && <div className='shrink-0'>{subheader}</div>}
        {/* `min-h-0` so this can actually shrink inside the flex column — without it the body keeps
            its content height and the cap above does nothing.

            `p-1 -m-1` is for the RING. `overflow-y-auto` clips at the padding edge, and the content
            here is exactly as wide as the box, so an outline drawn round a row landed flush against
            that edge and had its sides cut off — which reads as two horizontal lines rather than an
            outline. Six pixels of padding give the ring somewhere to land — it reaches 4px, so this
            is one step clear rather than exactly flush — and the negative margin hands the space
            back so nothing moves. The `editor` layout has no such trick: its body is a band the
            caller pads itself, so a ring has the caller's `px-4` to land in. */}
        <div className={`min-h-0 flex-1 overflow-y-auto ${isEditor ? '' : 'p-1.5 -m-1.5'}`}>{children}</div>
        {footer && <div className='shrink-0'>{footer}</div>}
      </div>
    </div>
  );
}
